import { useState, useCallback, useEffect, useReducer, useMemo, useRef } from 'react'
import * as R from 'ramda'
import useSWR, { mutate, cache } from 'swr'

import { getAxios } from 'api'
import reducer from './reducer'

export { mutate, cache }

/**
 * @typedef ExtraConfig
 * @property {boolean=} skip
 * @property {string|string[]} [key] unique key use to cache GET request
 */

/** @typedef {import('axios').AxiosRequestConfig & ExtraConfig} UseAxiosConfig */

/** @param {UseAxiosConfig} config */
export default function useAxios(config) {
  config.method = config.method || 'get'
  validateConfig(config)

  const [memoizedConfig, setMemoizedConfig] = useState(config)
  if (!R.equals(config, memoizedConfig)) {
    setMemoizedConfig(config)
  }

  const [state, dispatch] = useReducer(reducer, reducer.createInitialState(memoizedConfig.skip))

  /** @type {import('react').MutableRefObject<Promise<import('axios').AxiosResponse<any>>>} */
  const promiseRef = useRef()
  const hasFetchedRef = useRef(false)
  const fetch = useCallback(
    /** @param {UseAxiosConfig=} toMergeConfig */
    (toMergeConfig) => {
      if (promiseRef.current) {
        promiseRef.current.cancel()
      }
      hasFetchedRef.current = true
      dispatch({ type: 'FETCH' })

      const promise = getAxios()({
        ...memoizedConfig,
        ...toMergeConfig,
      })
        .then((response) => {
          dispatch({ type: 'FETCH_SUCCEEDED', data: response.data })
          return response
        })
        .catch(
          /**@param {Error|import('axios').AxiosError} error */
          (error) => {
            if (error.isAxiosError) {
              const data = error.response && error.response.data
              const errorMessage = data || (error.response && error.response.statusText)
              dispatch({ type: 'FETCH_FAILED', data: errorMessage })
            } else {
              dispatch({ type: 'FETCH_FAILED', data: error.message })
            }

            throw error
          }
        )

      promiseRef.current = promise
      return promise
    },
    [memoizedConfig]
  )

  const shouldUseSWR = isSaveMethod(memoizedConfig.method)
  const swrKey = memoizedConfig.key || memoizedConfig.url
  const [shouldSwrFetch, setShouldSwrFetch] = useState(!memoizedConfig.skip)
  const swrProps = useSWR(
    shouldUseSWR && shouldSwrFetch ? swrKey : null,
    () => fetch().then((response) => response.data),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  )

  useEffect(() => {
    if (!memoizedConfig.skip) {
      if (!shouldUseSWR) {
        fetch().catch(() => {})
      } else {
        setShouldSwrFetch(true)
      }
    }
  }, [fetch, memoizedConfig.skip, shouldUseSWR])

  const returnValues = useMemo(() => {
    return {
      ...state,
      fetch,
    }
  }, [fetch, state])

  const fetchForSwr = useCallback(() => {
    setShouldSwrFetch(true)
    const mutate = swrProps.mutate
    mutate()

    return promiseRef.current
  }, [swrProps.mutate])

  const [hasSeenValidating, setHasSeenValidating] = useState(false)
  if (swrProps.isValidating && !hasSeenValidating) {
    setHasSeenValidating(true)
  }

  const hasSwrError = swrProps.error !== undefined
  const hasSwrData = swrProps.data !== undefined && !swrProps.isValidating
  const isDedupedAndSettled =
    hasSeenValidating && !hasFetchedRef.current && (hasSwrError || hasSwrData)

  const swrReturnValues = useMemo(() => {
    return {
      data: swrProps.data,
      error: swrProps.error,
      loading: state.loading && !isDedupedAndSettled,
      fetch: fetchForSwr,
    }
  }, [fetchForSwr, state.loading, isDedupedAndSettled, swrProps.data, swrProps.error])

  if (!shouldUseSWR) {
    return returnValues
  } else {
    return swrReturnValues
  }
}

function validateConfig(config) {
  // Axios config doesn't require 'method' and would default the value to 'get'
  if (config.key && !isSaveMethod(config.method)) {
    throw Error(
      "useAxios only support caching result with GET request when specifying 'key' property"
    )
  }
}

function isSaveMethod(method) {
  return ['get', 'GET'].includes(method)
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/hooks/useAxios/index.js